import Box from '@/common/components/Box'
import {CurrencyTextField} from '@/common/components/CurrencyTextField'
import Select from '@/common/components/Select'
import useDebounce from '@/common/hooks/useDebounce'
import {Option} from '@/common/types/option'
import {cn} from '@/common/utils/cn'
import {formatCurrency} from '@/common/utils/formatters'
import {unFormatCurrency} from '@/common/utils/unFormatters'
import {useClientCompanyBranchAvailableToAllocateSummary} from '@/features/privateCompanies/hooks/useClientCompanyBranchAvailableToAllocateSummary'
import {GovernmentAllocationEntryProductCategoryValidationSchema} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/schemas/update.schema'
import {EntryProductFormItem} from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Update/components/EntryProductFormItem'
import {ClientCompanyAllowedCategoriesAndProductsTransformer} from '@/features/privateCompanies/services/allowedCategoriesAndProductsTransformer'
import {ClientCompanyAllowedCategoriesAndProducts, ClientCompanyAllowedCategoryAndProductData} from '@/features/privateCompanies/types/allowedCategoriesAndProducts.type'
import {
  CompanyGovernmentAllocationEntryProduct,
  CompanyGovernmentAllocationEntryProductCategory,
} from '@/features/privateCompanies/types/companyGovernmentAllocationEntry.type'
import {filterOptions} from '@/features/users/constants/filterOptions.constant'
import {BoxesIcon, SearchIcon} from 'lucide-react'
import {useMemo} from 'react'
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {FilterOptionOption} from 'react-select'

interface EntryProductCategoryProps {
  companyBranchId?: string
  entryProductCategory: CompanyGovernmentAllocationEntryProductCategory
  allowedCategorySummary: ClientCompanyAllowedCategoryAndProductData
  disabled: boolean
}

export const EntryProductCategory = ({
  companyBranchId,
  entryProductCategory,
  allowedCategorySummary,
  disabled,
}: EntryProductCategoryProps) => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: {errors},
  } = useFormContext<GovernmentAllocationEntryProductCategoryValidationSchema>()

  const [searchProduct, setSearchProduct] = useDebounce('')

  const {
    data: allowedCategoriesAndProducts,
    isPending: isAllowedCategoriesAndProductsPending,
  } = useClientCompanyBranchAvailableToAllocateSummary<ClientCompanyAllowedCategoriesAndProducts>({
    companyBranchId,
    filters: {
      search: searchProduct,
      productCategoryId: entryProductCategory?.product_category?.id,
    },
  });

  const allowedProductsOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsPending || !allowedCategoriesAndProducts || !allowedCategoriesAndProducts['products']
      ? []
      : ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({key: 'products'})(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsPending]);

  const {
    fields: entryProducts,
    append: appendEntryProduct,
    remove: removeEntryProduct,
  } = useFieldArray({
    control,
    name: 'entry_products',
  })

  const watchCurrentCategoryGrantedCredit = useWatch({
    control,
    name: 'total_value',
  });

  const availableTotal = Number(allowedCategorySummary?.available_total) || 0;
  const availableForAllocation = Math.max(availableTotal - unFormatCurrency(watchCurrentCategoryGrantedCredit), 0);

  const setProductCategoryTotalValue = () => {
    setTimeout(() => {
      const productCategoriesTotalValueSum = entryProducts.reduce((sum, _, index) => {
        const productTotalValue = watch(`entry_products.${index}.total_value`);

        return sum + (productTotalValue ? unFormatCurrency(productTotalValue) : 0);
      }, 0);

      setValue(`total_value`, formatCurrency(productCategoriesTotalValueSum));
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <div className="flex items-center gap-x-2">
          <BoxesIcon strokeWidth={1} />

          <p>{entryProductCategory?.product_category?.display_name}</p>
        </div>
      </div>

      <div className='flex flex-col gap-1'>
        <CurrencyTextField
          label="Valor total do serviço"
          {...register('total_value')}
          markAsRequired
          forceValue
          value={watch('total_value')}
          errorMessage={errors.total_value?.message || undefined}
          disabled={disabled || entryProducts.length > 0}
        />

        {allowedCategorySummary && (
          <div className='flex gap-3'>
            <p className='italic text-xs text-white-400'>
              Total possível: {allowedCategorySummary?.available_total ? formatCurrency(Number(allowedCategorySummary?.available_total) || 0) : '--'}
            </p>

            <p className='italic text-xs text-white-400'>
              Disponível para distribuir: {formatCurrency(availableForAllocation)}
            </p>

            <p className='italic text-xs text-white-400'>
              Total garantido: {allowedCategorySummary?.granted_total ? formatCurrency(Number(allowedCategorySummary?.granted_total) || 0) : '--'}
            </p>

            <p className='italic text-xs text-white-400'>
              Total inserido: {allowedCategorySummary?.entry_total ? formatCurrency(Number(allowedCategorySummary?.entry_total) || 0) : '--'}
            </p>
          </div>
        )}
      </div>

      <Box className="space-y-4 p-4">
        <Select
          placeholder={
            <div className={cn('flex items-center gap-x-2', !disabled && 'text-white-800')}>
              <SearchIcon />
              <p>Pesquisar produtos</p>
            </div>
          }
          isLoading={isAllowedCategoriesAndProductsPending}
          options={
            allowedProductsOptions?.filter((productOption) => {
              return !entryProducts?.some(({product}) => product?.id === productOption.value)
            }) || []
          }
          onInputChange={setSearchProduct}
          filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
          value=""
          onChange={(newValue) => {
            appendEntryProduct({
              product: {
                id: (newValue as Option).value,
                display_name: (newValue as ClientCompanyAllowedCategoryAndProductData).display_name,
                unit_for_quantity: (newValue as ClientCompanyAllowedCategoryAndProductData).unit_for_quantity,
                category: {
                  id: '',
                  display_name: '',
                  products_count: ''
                }
              },
              total_value: formatCurrency(0),
            });

            setProductCategoryTotalValue();
          }}
          isDisabled={disabled}
        />

        {entryProducts?.map((entryProduct, index) => (
          <EntryProductFormItem
            key={entryProduct?.product?.id}
            entryProductIndex={index}
            entryProduct={entryProduct as CompanyGovernmentAllocationEntryProduct}
            onDelete={() => {
              removeEntryProduct(index);
              setProductCategoryTotalValue();
            }}
            disabled={disabled}
            allowedProductSummary={allowedCategoriesAndProducts?.products?.[entryProduct?.product?.id]}
            onTotalValueChange={setProductCategoryTotalValue}
          />
        ))}
      </Box>
    </div>
  )
}
